import type { IntlShape } from 'react-intl';

import type { UserListItem } from 'src/apiClients/queries';
import type { DataTableColDef, DataTableRow } from 'src/components/data-table/DataTable.types';
import {
    createNaFallback,
    createStateFilterOptions,
    createStateLabel,
} from 'src/utils/table-formatters';

import { userTableI18n } from './user-table-formatters.i18n';

/** Shared row projection for user tables. */
export const mapUserRow = (user: UserListItem): DataTableRow => ({
    id: user.id,
    seequentUserId: user.seequentUserId,
    login: user.login,
    email: user.email,
    type: user.type,
    isSubscriptionOwner: user.isSubscriptionOwner,
    state: user.state,
});

/** Filter options for the user type column. */
export const createUserTypeFilterOptions = (intl: IntlShape) => [
    { label: intl.formatMessage(userTableI18n.typeImago), value: 'imago' },
    { label: intl.formatMessage(userTableI18n.typeSso), value: 'sso' },
    { label: intl.formatMessage(userTableI18n.typeSqid), value: 'sqid' },
    { label: intl.formatMessage(userTableI18n.typeInvited), value: 'invited' },
];

/** Column definitions shared across all user tables. */
export const createUserColumns = (intl: IntlShape): DataTableColDef[] => {
    const naFallback = createNaFallback(intl);
    const stateLabel = createStateLabel(intl);
    return [
        {
            field: 'id',
            headerName: intl.formatMessage(userTableI18n.uuid),
            width: 150,
            valueFormatter: naFallback,
        },
        {
            field: 'seequentUserId',
            headerName: intl.formatMessage(userTableI18n.seequentId),
            width: 150,
            valueFormatter: naFallback,
        },
        {
            field: 'login',
            headerName: intl.formatMessage(userTableI18n.login),
            flex: 2,
            minWidth: 150,
            valueFormatter: naFallback,
        },
        {
            field: 'email',
            headerName: intl.formatMessage(userTableI18n.email),
            flex: 2,
            minWidth: 150,
            valueFormatter: naFallback,
        },
        {
            field: 'type',
            headerName: intl.formatMessage(userTableI18n.type),
            width: 100,
            filterOptions: createUserTypeFilterOptions(intl),
            valueFormatter: naFallback,
        },
        {
            field: 'isSubscriptionOwner',
            headerName: intl.formatMessage(userTableI18n.owner),
            width: 90,
            filterOptions: [
                { label: intl.formatMessage(userTableI18n.owner), value: true },
                { label: intl.formatMessage(userTableI18n.nonOwner), value: false },
            ],
            valueFormatter: (value) =>
                value === true
                    ? intl.formatMessage(userTableI18n.owner)
                    : intl.formatMessage(userTableI18n.nonOwner),
        },
        {
            field: 'state',
            headerName: intl.formatMessage(userTableI18n.status),
            width: 85,
            filterOptions: createStateFilterOptions(intl),
            valueFormatter: stateLabel,
        },
    ];
};
